import type { UseQueryResult } from '@tanstack/react-query';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'lodash';
import pino from 'pino';

import type { CardSource, GetTeamApproversParams } from '../repositories/paymentRepository';
import { PaymentRepository } from '../repositories/paymentRepository';
import { queryClient } from '../router/queryClient';
import i18n from '../utils/i18n';
import { useZustand } from '../zustandStore';
import type { CardFormData, CardSourceInfo, CreditCard, RequestType as RequestType } from '../zustandStore/slices';
import { buildAmexCardSource, buildAmexPaymentInformation } from '../zustandStore/slices';

const logger = pino();

export const paymentKeys = {
	getCardSources: 'getCardSources',
	getTeamApprovers: 'getTeamApprovers',
	getCardSource: 'getCardSource',
	getTeamsForCardSource: 'getTeamsForCardSource',
	setCardName: 'setCardName',
	addCardSourceToTeam: 'addCardSourceToTeam',
	removeCardSourceFromTeam: 'removeCardSourceFromTeam',
	getEnrollmentTokenV2: 'getEnrollmentTokenV2',
	getVirtualCardToken: (documentId: RequestType['requestId']) => ['getVirtualCardToken', documentId] as const,
	getVirtualCard: (documentId: RequestType['requestId']) => ['getVirtualCard', documentId] as const,
	getCurrencyAndTotal: (currency: string, value: string | number) => ['getCurrencyAndTotal', currency, value] as const,
	getVirtualCardInfo: (documentId: RequestType['requestId']) => ['getVirtualCardInfo', documentId] as const,
	getCompanyCardSources: (includeTeamsCount = true) => ['companyCardSources', { includeTeamsCount }] as const,
	getTeamCardSources: (params: UseGetTeamCardSourcesQuery) => ['teamCardSources', params] as const,
	getVirtualCardData: (documentId: RequestType['requestId'], isCardOwner) =>
		['getVirtualCardData', documentId, isCardOwner] as const,
};

export const paymentParams = {
	includeTeamsCount: true,
};

type BillingAddress = {
	addressLine1: string | null | undefined;
	city: string | null | undefined;
	countryCode: string | null | undefined;
	region: string | null | undefined;
	zipCode: string | null | undefined;
};

export type GetTeamCardSourcesQueryResultData = {
	billingAddress: BillingAddress;
	currency: string;
	id: string;
	label: string;
	lastDigits: string | number;
	name: string;
	provider: string;
	providerLabel: string;
	status: string;
	teamsCount: string;
};

export type CardSourcesQueryResult = {
	data: GetTeamCardSourcesQueryResultData[];
	error: unknown;
	isLoading: boolean;
};

export const useGetCardSourcesQuery = ({
	includeTeamsCount = true,
	status = '',
	teamId = '',
} = {}): UseQueryResult<CardSourcesQueryResult> => {
	const params = {
		includeTeamsCount,
		status,
	};

	return useQuery({
		queryKey: [paymentKeys.getCardSources, { includeTeamsCount, status, teamId }, params],
		queryFn: () => PaymentRepository.getCardSources(params, teamId),
	});
};

export const useSetCardNameQuery = ({ shouldSetOnCompanyCard = true }) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: PaymentRepository.setCardName,
		onMutate: async ({ cardSourceId, name, teamId }) => {
			await (shouldSetOnCompanyCard
				? queryClient.cancelQueries({ queryKey: [paymentKeys.getCompanyCardSources(true)] })
				: queryClient.cancelQueries({
						queryKey: [
							paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: teamId || '', isEnabled: true }),
						],
					}));
			const previousData: CreditCard[] | GetTeamCardSourcesQueryResultData[] | undefined = queryClient.getQueryData(
				shouldSetOnCompanyCard
					? [paymentKeys.getCompanyCardSources(true)]
					: [paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: teamId || '', isEnabled: true })],
			);

			const previousCardSourceData: CreditCard | GetTeamCardSourcesQueryResultData | undefined = [
				...(previousData ?? []),
			].find(data => data && data.id === cardSourceId);
			if (previousCardSourceData) {
				queryClient.setQueryData(
					shouldSetOnCompanyCard
						? [paymentKeys.getCompanyCardSources(true)]
						: [paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: teamId || '', isEnabled: true })],
					(prev: CreditCard[] | GetTeamCardSourcesQueryResultData[]) => {
						const updated = prev.map(data => {
							if (data.id === cardSourceId) {
								return { ...data, name };
							}
							return data;
						});
						return updated;
					},
				);
			}

			return { previousCardSourceData };
		},
		onError: (err, variables, context) => {
			logger.error(err);
			if (context?.previousCardSourceData) {
				queryClient.setQueryData(
					shouldSetOnCompanyCard
						? [paymentKeys.getCompanyCardSources(true)]
						: [
								paymentKeys.getTeamCardSources({
									includeTeamsCount: true,
									teamId: variables.teamId || '',
									isEnabled: true,
								}),
							],
					(prev: { data?: CreditCard[] | GetTeamCardSourcesQueryResultData[] }) => {
						const updated = prev.data?.map(data =>
							data.id === context.previousCardSourceData?.id ? context.previousCardSourceData : data,
						);
						return { ...prev, data: updated };
					},
				);
			}
		},
	});
};

export const useAddCardSourceToTeamQuery = () => {
	const queryClient = useQueryClient();
	return useMutation<unknown, unknown, CardSource>({
		mutationFn: PaymentRepository.addCardSourceToTeam,
		onMutate: async ({ cardSourceId }) => {
			await queryClient.cancelQueries({ queryKey: [paymentKeys.getCompanyCardSources(true)] });

			const prevData: CreditCard[] | undefined = queryClient.getQueryData([paymentKeys.getCompanyCardSources(true)]);

			if (prevData) {
				const updatedData = prevData.map(card => {
					if (card.id === cardSourceId) {
						return { ...card, teamsCount: (Number.parseInt(card.teamsCount, 10) + 1).toString() };
					}
					return card;
				});

				queryClient.setQueryData([paymentKeys.getCompanyCardSources(true)], updatedData);
			}
		},
		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [paymentKeys.getCompanyCardSources] });
			void queryClient.invalidateQueries({ queryKey: [paymentKeys.getTeamsForCardSource] });
		},
	});
};

export const useRemoveCardSourceFromTeamQuery = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: PaymentRepository.removeCardSourceFromTeam,
		onMutate: async ({ cardSourceId }) => {
			await queryClient.cancelQueries({ queryKey: [paymentKeys.getCompanyCardSources(true)] });

			const prevData: CreditCard[] | undefined = queryClient.getQueryData([paymentKeys.getCompanyCardSources(true)]);

			if (prevData) {
				const updatedData = prevData.map(card => {
					if (card.id === cardSourceId) {
						return { ...card, teamsCount: (Number.parseInt(card.teamsCount, 10) - 1).toString() };
					}
					return card;
				});

				queryClient.setQueryData([paymentKeys.getCompanyCardSources(true)], updatedData);
			}
		},
		onSettled: () => {
			void queryClient.invalidateQueries({ queryKey: [paymentKeys.getCompanyCardSources] });
			void queryClient.invalidateQueries({ queryKey: [paymentKeys.getTeamsForCardSource] });
		},
	});
};

export const useGetTeamApproversQuery = ({ excludeSelf, excludeOutOfOffice, teamId }: GetTeamApproversParams) => {
	const { setTeamApprovers } = useZustand();
	return useQuery({
		queryKey: [paymentKeys.getTeamApprovers, excludeSelf, teamId, excludeOutOfOffice, setTeamApprovers],
		queryFn: async () => {
			const result = await PaymentRepository.getTeamApprovers({
				excludeSelf,
				excludeOutOfOffice: excludeOutOfOffice || false,
				teamId,
			});
			setTeamApprovers(result);
			return result;
		},
		enabled: Boolean(teamId),
	});
};

const getCompanyCardSourcesQuery = ({ includeTeamsCount = true, isEnabled = true }) => ({
	queryKey: [paymentKeys.getCompanyCardSources(includeTeamsCount)],
	queryFn: () =>
		PaymentRepository.getCompanyCardSources({
			includeTeamsCount,
		}),
	enabled: isEnabled ?? true,
});

export const useGetCompanyCardSourcesQuery = ({ includeTeamsCount = true, isEnabled = true }) => {
	return useQuery(getCompanyCardSourcesQuery({ includeTeamsCount, isEnabled }));
};

export type UseGetTeamCardSourcesQuery = {
	teamId?: string;
	includeTeamsCount: boolean;
	isEnabled?: boolean;
};

export const useGetTeamCardSourcesQuery = (params: UseGetTeamCardSourcesQuery) => {
	const { setTeamCards } = useZustand();
	return useQuery({
		queryKey: [paymentKeys.getTeamCardSources(params), setTeamCards],
		queryFn: async () => {
			const result = await PaymentRepository.getTeamCardSources(params);
			setTeamCards(result);
			return result;
		},
		enabled: params.isEnabled ?? true,
	});
};

type CardHolder = {
	name: string;
};

export type GetCardSourceData = {
	billingAddress: BillingAddress;
	billingCurrency: string;
	id: string;
	lastDigits: string;
	name: string;
	provider: string;
	status: string;
	cardHolder?: CardHolder;
};

export const useGetCardSourceQuery = ({
	cardSourceId,
	includeCardHolder,
	teamId,
}: CardSource & { includeCardHolder: boolean }) => {
	const { setCardSource } = useZustand();
	return useQuery({
		queryKey: [paymentKeys.getCardSource, cardSourceId, includeCardHolder, teamId, setCardSource],
		queryFn: async () => {
			const result = await PaymentRepository.getCardSource({ cardSourceId, includeCardHolder, teamId: teamId || '' });
			setCardSource(result);
			return result;
		},
		enabled: !!cardSourceId,
	});
};

export const useGetTeamsForCardSourceQuery = ({ cardSourceId }) => {
	const { setCardSourceTeams } = useZustand();
	return useQuery({
		queryKey: [paymentKeys.getTeamsForCardSource, cardSourceId, setCardSourceTeams],
		queryFn: async () => {
			const result = await PaymentRepository.getTeamsForCardSource(cardSourceId);
			setCardSourceTeams(result);
			return result;
		},
		enabled: Boolean(cardSourceId),
	});
};

async function enrollCard(enrollData: CardFormData, teamId: string) {
	if (!enrollData || isEmpty(enrollData)) {
		throw new TypeError('formData is empty');
	}

	const data = await PaymentRepository.getEnrollmentToken({ teamId });
	const enrollment = {
		enrollmentToken: data.token,
		paymentInformation: buildAmexPaymentInformation(enrollData),
	};

	const result: CardSourceInfo = await PaymentRepository.enrollWithAmex(enrollment);
	const cardSource = buildAmexCardSource(enrollData, result);
	return { cardSource };
}

const mapUiCardDataToBaseStructure = (obj: CardFormData) => {
	if (!obj) {
		return null;
	}
	return {
		billingAddress: {
			addressLine1: obj.address,
			city: obj.city,
			countryCode: obj.countryCode,
			region: obj.regionCode,
			zipCode: obj.zipCode,
		},
		id: obj.id,
		lastDigits: obj.cardNumber.slice(-4),
		name: obj.name,
		status: 'IN_PROGRESS',
		label: `${obj.name}, ···· ${obj.cardNumber.slice(-4)}`,
		teamsCount: '0',
		userId: obj.userId,
	};
};

const mapUiCardDataToAmexStructure = (obj: CardFormData) => {
	const baseStructure = mapUiCardDataToBaseStructure(obj);
	if (!baseStructure) {
		return null;
	}
	return {
		...baseStructure,
		billingAddress: {
			...baseStructure.billingAddress,
			countryCode: 'US',
		},
		billingCurrency: obj.billingCurrency || 'USD',
		provider: 'AMEX',
		providerLabel: 'American Express',
	};
};

export const useEnrollCardV2Query = () => {
	return useMutation({
		mutationFn: ({ enrollData, teamId }: { enrollData: CardFormData; teamId: string }) =>
			enrollCard(enrollData, teamId),
		onMutate: async variables => {
			// we need this as back-end returns old data after responding ok
			await (variables.teamId
				? queryClient.cancelQueries({
						queryKey: [
							paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: variables.teamId, isEnabled: true }),
						],
					})
				: queryClient.cancelQueries({ queryKey: [paymentKeys.getCompanyCardSources(true)] }));
			const previousData: CreditCard[] | GetTeamCardSourcesQueryResultData[] | undefined = queryClient.getQueryData(
				variables.teamId
					? [paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: variables.teamId, isEnabled: true })]
					: [paymentKeys.getCompanyCardSources(true)],
			);

			if (previousData) {
				queryClient.setQueryData(
					variables.teamId
						? [paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: variables.teamId, isEnabled: true })]
						: [paymentKeys.getCompanyCardSources(true)],
					(prev: CreditCard[] | GetTeamCardSourcesQueryResultData[]) => {
						const updated = [...(prev ?? []), mapUiCardDataToAmexStructure(variables.enrollData)];
						return updated;
					},
				);
			}

			return { previousData };
		},
		onError: (_error, variables, context) => {
			if (context?.previousData) {
				queryClient.setQueryData(
					variables.teamId
						? [paymentKeys.getTeamCardSources({ includeTeamsCount: true, teamId: variables.teamId, isEnabled: true })]
						: [paymentKeys.getCompanyCardSources(true)],
					context.previousData,
				);
			}
		},
	});
};

export const virtualCardDataQuery = (documentId: RequestType['requestId'], isCardOwner) => {
	const getVirtualCardData = async () => {
		if (!documentId) {
			throw new Error('documentId is required');
		}

		const cardInfo = await PaymentRepository.getVirtualCardInfo({ documentId });
		const isCardActive = cardInfo?.status === 'ACTIVE' || cardInfo?.status === 'IN_PROGRESS';

		if (isCardActive && isCardOwner) {
			const { token, tokenSource } = await PaymentRepository.getVirtualCardToken(documentId);
			return PaymentRepository.getVirtualCard(token, tokenSource);
		}
		return cardInfo;
	};

	return queryOptions({
		queryKey: paymentKeys.getVirtualCardData(documentId, isCardOwner),
		queryFn: getVirtualCardData,
		enabled: Boolean(documentId),
		staleTime: Infinity,
	});
};

export const currencyAndTotalQuery = (currency: string, value: number | string) => {
	return queryOptions({
		queryKey: paymentKeys.getCurrencyAndTotal(currency, value),
		queryFn: async () => {
			await i18n.setLanguage('en');
			const currencySymbol = i18n.getCurrencySymbol(currency || 'USD');
			const numericValue = typeof value === 'string' ? i18n.parseAmount(value) : value;
			const newValue = i18n.formatNumber(numericValue);
			return `${currencySymbol}${newValue}`;
		},
		enabled: Boolean(currency) && value != null,
	});
};
